import type { SkinGachaBannerResponse, SkinGachaRollResponse } from '@kenocyber/shared';

/**
 * The Cash roulette's interface, as one value (gate V24C).
 *
 * ## Why a phase and not a handful of booleans
 *
 * Because `busy`, `confirming`, `revealing` and `failed` as four independent
 * flags can all be true, and when they are the screen shows a confirmation over
 * a reveal over an error while a request is in flight. The gacha panel that
 * already ships learned this the expensive way with two overlay booleans. One
 * `phase` makes the contradictory combinations not exist to be reached - not by
 * a double click, not by a race, not by a late response.
 *
 * ## Nothing here is optimistic
 *
 * There is no state in which a result exists before the server answered. The
 * only event that can produce one is `rolled`, the only phase it is accepted
 * from is `submitting`, and `submitting` is only reachable from `confirming` or
 * from a retry of an intention that was already sent. A transport failure, a
 * refusal, a conflict or an empty wallet therefore cannot show a prize - there
 * is no path through this reducer that puts one on screen.
 *
 * ## The animation runs AFTER the answer
 *
 * `revealing` is entered by `rolled` and by nothing else. The theatre is over a
 * copy that is already in the vault, so skipping it, closing it or reloading
 * changes nothing about what the account owns.
 *
 * ## One intention, one key
 *
 * The key is minted when a player confirms and is kept for as long as THAT
 * attempt is being retried. An uncertain answer keeps the intention, so
 * repeating it is a replay rather than a second purchase; a settled attempt
 * clears it, so the next confirmation is a new intention with a new key. Both
 * halves matter: a key that changed on every attempt would charge twice, and a
 * key that never changed would make a deliberate second roll look like a replay.
 */

export type SkinGachaPhase =
  /** No banner is offered. The shipped build's permanent answer. */
  | 'unavailable'
  /** Asking. */
  | 'loading'
  /** An offer exists and nothing is open over it. */
  | 'ready'
  /** The full price and odds panel is open. Nothing has been charged. */
  | 'details'
  /** The confirmation is up. Still nothing has been charged. */
  | 'confirming'
  /** A roll is in flight. Exactly one economic operation may exist here. */
  | 'submitting'
  /** The server answered; the anticipation and reveal are playing. */
  | 'revealing'
  /** The reveal is over and the copy is on screen. */
  | 'result'
  /** The wallet does not cover the price. No charge happened. */
  | 'insufficient'
  /** Something recoverable went wrong BEFORE anything was charged. */
  | 'failed'
  /** The answer never arrived. The same intention may be repeated. */
  | 'uncertain';

/** What one attempt is: a banner, the version and fingerprint shown, a key. */
export interface SkinGachaIntent {
  readonly idempotencyKey: string;
  readonly bannerId: string;
  readonly bannerVersion: number;
  readonly bannerFingerprint: string;
}

export interface SkinGachaState {
  readonly phase: SkinGachaPhase;
  /** The offer, exactly as the server published it. Never edited locally. */
  readonly banner: SkinGachaBannerResponse | null;
  /** The confirmed answer. Non-null only while revealing or showing a result. */
  readonly result: SkinGachaRollResponse | null;
  /** The attempt being made or awaiting a retry. Null once it settles. */
  readonly intent: SkinGachaIntent | null;
  /** A sentence for the player. Never a status code, never a stack. */
  readonly message: string | null;
}

export const SKIN_GACHA_INITIAL: SkinGachaState = Object.freeze({
  phase: 'loading',
  banner: null,
  result: null,
  intent: null,
  message: null,
});

export type SkinGachaEvent =
  | { readonly type: 'reload' }
  | { readonly type: 'banner_available'; readonly banner: SkinGachaBannerResponse }
  | { readonly type: 'banner_unavailable' }
  | { readonly type: 'banner_failed'; readonly message: string }
  | { readonly type: 'open_details' }
  | { readonly type: 'close_details' }
  | { readonly type: 'request_confirmation' }
  | { readonly type: 'cancel_confirmation' }
  | { readonly type: 'submit'; readonly intent: SkinGachaIntent }
  | { readonly type: 'retry' }
  | { readonly type: 'rolled'; readonly result: SkinGachaRollResponse }
  | { readonly type: 'insufficient'; readonly message: string }
  | { readonly type: 'rejected'; readonly message: string }
  | { readonly type: 'uncertain'; readonly message: string }
  | { readonly type: 'reveal_finished' }
  | { readonly type: 'dismiss' };

/**
 * The phases a background re-read must not disturb.
 *
 * A re-read happens whenever the panel opens and after every attempt settles,
 * and the answer arrives whenever it arrives. Letting it decide the phase would
 * wipe the sentence a player is reading - which is exactly how "saldo
 * insuficiente" disappeared a beat after it appeared - or cut a reveal off from
 * the copy it is showing. So only the two phases that have nothing to protect
 * are moved by one.
 */
const REPLACEABLE: readonly SkinGachaPhase[] = ['loading', 'unavailable'];

/**
 * The phases from which a player may open the odds.
 *
 * `ready`, plus the two terminal outcomes of a previous attempt: somebody who
 * was told their wallet was short, or that the offer had changed, must be able
 * to look at the table again without reloading the page. Never from
 * `uncertain`, where the only honest next moves are repeating that same
 * intention or leaving it alone.
 */
const CAN_OPEN_DETAILS: readonly SkinGachaPhase[] = ['ready', 'insufficient', 'failed'];

/**
 * The next state. Total, pure and free of I/O.
 *
 * An event that does not belong to the current phase returns the SAME object
 * rather than a mutated one - identity is the signal a caller can assert on,
 * and "ignored" is a real outcome here rather than an oversight. A second
 * `submit` while one is in flight is exactly that case, and it is how a double
 * click becomes one economic operation before React has even re-rendered.
 */
export function skinGachaReduce(state: SkinGachaState, event: SkinGachaEvent): SkinGachaState {
  switch (event.type) {
    case 'reload': {
      /*
       * Only the FIRST read shows a loading state.
       *
       * Every later one is a background re-read - the panel opens, an attempt
       * settles, the balance moved - and turning the screen into "carregando…"
       * for one of those would throw away whatever the player is currently
       * reading. With an offer already in hand there is nothing to wait for on
       * screen, so the phase does not move at all.
       */
      return state.banner === null && state.phase !== 'loading'
        ? { ...state, phase: 'loading', message: null }
        : state;
    }

    case 'banner_available': {
      // The offer is replaced underneath whatever is happening; only a screen
      // with nothing of its own to protect changes phase because of it.
      return REPLACEABLE.includes(state.phase)
        ? { ...state, phase: 'ready', banner: event.banner, message: null }
        : { ...state, banner: event.banner };
    }

    case 'banner_unavailable': {
      /*
       * The offer went away.
       *
       * Never mid-purchase: an answer is still owed to a request that left, and
       * a reveal is showing a copy that exists whatever the banner does now.
       */
      if (state.phase === 'submitting' || state.phase === 'revealing' || state.phase === 'result') {
        return state;
      }
      return { phase: 'unavailable', banner: null, result: null, intent: null, message: null };
    }

    case 'banner_failed': {
      // A failed re-read never overwrites the outcome of an attempt.
      if (!REPLACEABLE.includes(state.phase)) {
        return state;
      }
      return { ...state, phase: 'failed', message: event.message };
    }

    case 'open_details':
      return CAN_OPEN_DETAILS.includes(state.phase) && state.banner
        ? { ...state, phase: 'details', message: null }
        : state;

    case 'close_details':
      /*
       * A player may always leave without buying, and leaving must cost nothing.
       * Reachable from the confirmation too, which is what makes "fechar sem
       * comprar" true at every step of the flow rather than only at the first.
       */
      return state.phase === 'details' || state.phase === 'confirming'
        ? { ...state, phase: 'ready', message: null }
        : state;

    case 'request_confirmation':
      /*
       * Only from the details panel. A confirmation that could be opened from
       * `ready` would be a charge one click away from a screen that had not
       * yet shown the odds - which is the whole thing this gate is about.
       */
      return state.phase === 'details' ? { ...state, phase: 'confirming' } : state;

    case 'cancel_confirmation':
      return state.phase === 'confirming' ? { ...state, phase: 'details', message: null } : state;

    case 'submit': {
      // The ONLY door into an economic operation, and it is one phase wide.
      if (state.phase !== 'confirming') {
        return state;
      }
      return { ...state, phase: 'submitting', intent: event.intent, message: null };
    }

    case 'retry': {
      /*
       * The same intention, sent again.
       *
       * Only from `uncertain`, and only with the key that was already used: the
       * request may have committed, so repeating it has to be a replay. A new
       * key here would be a second purchase for a player who clicked "tentar de
       * novo" after a dropped connection.
       */
      if (state.phase !== 'uncertain' || !state.intent) {
        return state;
      }
      return { ...state, phase: 'submitting', message: null };
    }

    case 'rolled': {
      // Accepted from `submitting` alone. There is no other way to a prize.
      if (state.phase !== 'submitting') {
        return state;
      }
      return {
        ...state,
        phase: 'revealing',
        result: event.result,
        // Settled: the next confirmation is a new intention with a new key.
        intent: null,
        message: null,
      };
    }

    case 'insufficient': {
      if (state.phase !== 'submitting') {
        return state;
      }
      // A refusal moved nothing, so the attempt is over rather than retryable.
      return {
        ...state,
        phase: 'insufficient',
        intent: null,
        result: null,
        message: event.message,
      };
    }

    case 'rejected': {
      if (state.phase !== 'submitting') {
        return state;
      }
      /*
       * The server said no, definitely: a changed banner, a conflicting key, an
       * expired session. Nothing was charged and nothing is worth repeating
       * under the same key, so the intention is dropped and the player starts a
       * new one against a re-read offer.
       */
      return { ...state, phase: 'failed', intent: null, result: null, message: event.message };
    }

    case 'uncertain': {
      if (state.phase !== 'submitting') {
        return state;
      }
      /*
       * The request left and no answer came back. It MAY have committed, so the
       * intention is kept: repeating it is a replay that returns the same copy
       * and charges once. This is the one failure that keeps its key.
       */
      return { ...state, phase: 'uncertain', result: null, message: event.message };
    }

    case 'reveal_finished':
      return state.phase === 'revealing' ? { ...state, phase: 'result' } : state;

    case 'dismiss': {
      /*
       * Back to the ODDS, with the result let go.
       *
       * Not to `ready`. A player who closes a reveal is standing where they
       * were before the charge, and that place has the price and every chance
       * on it - so the next purchase begins from a screen that is already
       * showing what it costs, rather than from a card they must re-open.
       *
       * The browser gate found this the expensive way: closing a reveal
       * unmounted the panel, and the second roll of a loop waited for a button
       * that no longer existed. It is a real defect in the flow, not a
       * property of the test.
       *
       * Never from `submitting`: a panel that could be dismissed mid-flight
       * would leave an economic operation with nobody to receive its answer.
       */
      if (state.phase === 'submitting') {
        return state;
      }
      return {
        ...state,
        phase: state.banner ? 'details' : 'unavailable',
        result: null,
        message: null,
      };
    }

    default: {
      /*
       * Exhaustive by construction: a new event with no case makes this line a
       * type error rather than a silent no-op at runtime.
       */
      const unreachable: never = event;
      return unreachable;
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Questions the interface asks of a state                                   */
/* -------------------------------------------------------------------------- */

/** Whether an economic request is in flight. Exactly one phase says yes. */
export function isCharging(state: SkinGachaState): boolean {
  return state.phase === 'submitting';
}

/** Whether a prize is on screen. Only ever after the server answered. */
export function hasResult(state: SkinGachaState): boolean {
  return (state.phase === 'revealing' || state.phase === 'result') && state.result !== null;
}

/**
 * What the wallet holds after this purchase, or null when it cannot be said.
 *
 * `bigint` throughout, from the two decimal strings the server sent. A balance
 * is a currency amount and this codebase does not let one become a float, not
 * even to print it. Null when the balance does not cover the price - the screen
 * says "saldo insuficiente" rather than drawing a negative number.
 */
export function balanceAfter(banner: SkinGachaBannerResponse): bigint | null {
  const balance = BigInt(banner.balance);
  const price = BigInt(banner.priceCash);
  return balance >= price ? balance - price : null;
}

/** Whether the caller can pay for one roll right now. */
export function canAfford(banner: SkinGachaBannerResponse): boolean {
  return BigInt(banner.balance) >= BigInt(banner.priceCash);
}
